import { useMemo, useState } from "react";
import type { EngineHeroProps, EngineModule, InteractiveNode } from "../types";
import { EnergyNode } from "../living/EnergyNode";
import { LivingBackdrop } from "../living/LivingBackdrop";

/**
 * KAfrequency's original visual language — a living golden-ratio spiral of
 * harmonic bands. Nodes (bands) breathe on their own resonance; hovering /
 * focusing a band pulses its wave interference ring. No triangles, no rigid
 * chakra diagrams.
 */

// Seven harmonic bands — from densest to most subtle.
const BANDS = [
  { key: "root",     label: "Root Frequency",       hue: 350, order: 1 },
  { key: "flow",     label: "Flow Frequency",       hue:  20, order: 2 },
  { key: "will",     label: "Will Frequency",       hue:  48, order: 3 },
  { key: "heart",    label: "Heart Frequency",      hue: 140, order: 4 },
  { key: "voice",    label: "Voice Frequency",      hue: 200, order: 5 },
  { key: "vision",   label: "Vision Frequency",     hue: 250, order: 6 },
  { key: "crown",    label: "Crown Frequency",      hue: 300, order: 7 },
];

function polar(cx: number, cy: number, r: number, angleRad: number) {
  return { x: cx + Math.cos(angleRad) * r, y: cy + Math.sin(angleRad) * r };
}

function Hero({ nodes, onSelect, selectedKey }: EngineHeroProps) {
  const [hover, setHover] = useState<string | null>(null);
  const active = hover ?? selectedKey ?? null;
  const cx = 260, cy = 260;

  // Merge DB nodes with the canonical band list so unknown bands still render
  const merged = useMemo(() => {
    const byKey = new Map<string, InteractiveNode>();
    for (const n of nodes) byKey.set(n.key, n);
    return BANDS.map((b) => ({ band: b, node: byKey.get(b.key) ?? null }));
  }, [nodes]);

  // Golden-ratio spiral samples for the ambient path
  const spiralD = useMemo(() => {
    const PHI = (1 + Math.sqrt(5)) / 2;
    const steps = 240, turns = 3.2, maxR = 220;
    let d = "";
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const angle = t * turns * Math.PI * 2;
      const r = Math.pow(t, 1 / PHI) * maxR;
      const p = polar(cx, cy, r, angle);
      d += (i === 0 ? "M" : "L") + p.x.toFixed(2) + "," + p.y.toFixed(2) + " ";
    }
    return d;
  }, []);

  return (
    <div className="relative overflow-hidden rounded-2xl border hairline bg-obsidian/60">
      <LivingBackdrop hue={48} density={80} />
      <div className="relative z-10 grid gap-4 p-4 md:grid-cols-[1fr,300px]">
        <div className="mx-auto w-full max-w-[520px]">
          <svg viewBox="0 0 520 520" className="h-auto w-full" role="img" aria-label="KAfrequency living spiral">
            {/* Ambient golden spiral */}
            <path d={spiralD} fill="none" stroke="hsl(48 85% 60% / 0.35)" strokeWidth={1}
              className="motion-safe:animate-[energy-rotate_120s_linear_infinite]"
              style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }} />

            {/* Concentric wave interference rings */}
            {[80, 130, 180, 230].map((r) => (
              <circle key={r} cx={cx} cy={cy} r={r} fill="none"
                stroke="hsl(48 85% 60% / 0.18)" strokeWidth={1} />
            ))}

            {/* Bands as breathing nodes arranged along the spiral */}
            {merged.map(({ band }, i) => {
              const t = (i + 1) / (merged.length + 1);
              const angle = t * Math.PI * 1.8 - Math.PI / 2;
              const r = 80 + t * 150;
              const p = polar(cx, cy, r, angle);
              const isActive = active === band.key;
              return (
                <g key={band.key}
                  onMouseEnter={() => setHover(band.key)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(band.key)}
                  onBlur={() => setHover(null)}
                >
                  <EnergyNode
                    cx={p.x} cy={p.y} r={28}
                    hue={band.hue}
                    label={band.label.split(" ")[0]}
                    active={isActive}
                    onActivate={() => {
                      const dbNode = nodes.find((n) => n.key === band.key);
                      if (dbNode) onSelect?.(dbNode);
                      else onSelect?.({
                        id: band.key, key: band.key, kind: "region",
                        label: band.label,
                        description: null,
                        payload: { order: band.order, color: `hsl(${band.hue} 85% 60%)` },
                        lesson_id: null,
                      });
                    }}
                  />
                </g>
              );
            })}
          </svg>
        </div>

        <aside className="rounded-xl border hairline bg-obsidian/70 p-4 text-sm">
          {active ? (() => {
            const found = BANDS.find((b) => b.key === active);
            return (
              <>
                <div className="text-xs uppercase tracking-[0.3em] text-gold">Frequency Band</div>
                <p className="mt-2 font-serif text-lg text-gold-luminous">{found?.label}</p>
                <p className="mt-2 text-foreground/75">
                  A resonant band in the KAfrequency field. Study its rhythm, its
                  breath and how it converses with adjacent bands.
                </p>
              </>
            );
          })() : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Living Spiral</div>
              <p className="mt-2 text-foreground/75">
                Every band is a living harmonic. Move across the spiral to feel
                how frequency layers into experience.
              </p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

export const kafrequencyEngine: EngineModule = {
  label: "KAfrequency — Living Spiral",
  Hero,
};
